import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { useTheme } from '../context/ThemeProvider';
import { useUser } from '../context/UserContext';
import { db } from '../lib/firebase';
import { COLLECTION_RAG_CHUNKS } from '../lib/firestorePaths';
import { indexUserRag, indexUserRagErrorMessage } from '../lib/indexUserRag';
import authStyles from '../pages/auth/authPages.module.css';
import { formatDateLong } from '../utils/formatters';
import { GlobalButton } from './GlobalButton';

function formatIndexSuccess(chunksWritten: number, skipped: number): string {
  if (chunksWritten === 0 && skipped === 0) {
    return 'No data to index yet. Add portfolio, registry, or bank data first.';
  }
  if (chunksWritten === 0) {
    return `Index up to date (${skipped} chunk${skipped === 1 ? '' : 's'} unchanged).`;
  }
  const writtenLabel = `${chunksWritten} chunk${chunksWritten === 1 ? '' : 's'} updated`;
  const skippedLabel =
    skipped > 0 ? `, ${skipped} unchanged` : '';
  return `Indexed ${writtenLabel}${skippedLabel}.`;
}

export function RagIndexPanel() {
  const { user } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const [lastIndexedAt, setLastIndexedAt] = useState<string | null>(null);
  const [loadingLastIndexed, setLoadingLastIndexed] = useState(Boolean(user?.uid));
  const [indexing, setIndexing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) {
      setLastIndexedAt(null);
      setLoadingLastIndexed(false);
      return;
    }

    setLoadingLastIndexed(true);
    const q = query(collection(db, COLLECTION_RAG_CHUNKS), where('userId', '==', user.uid));

    return onSnapshot(
      q,
      (snap) => {
        let latest: string | null = null;
        for (const doc of snap.docs) {
          const updatedAt = doc.get('updatedAt');
          if (typeof updatedAt === 'string' && updatedAt.trim()) {
            if (!latest || updatedAt > latest) {
              latest = updatedAt.trim();
            }
          }
        }
        setLastIndexedAt(latest);
        setLoadingLastIndexed(false);
      },
      (snapshotError) => {
        console.error(snapshotError);
        setLoadingLastIndexed(false);
      }
    );
  }, [user?.uid]);

  const handleReindex = useCallback(async () => {
    setIndexing(true);
    setSuccess(null);
    setError(null);

    try {
      const result = await indexUserRag();
      setSuccess(formatIndexSuccess(result.chunksWritten, result.skipped));
    } catch (err) {
      setError(indexUserRagErrorMessage(err));
    } finally {
      setIndexing(false);
    }
  }, []);

  const lastIndexedLabel = loadingLastIndexed
    ? 'Loading…'
    : lastIndexedAt
      ? formatDateLong(lastIndexedAt)
      : 'Not indexed yet';

  const repeatIcon = (
    <img
      src={isDarkMode ? '/assets/icons/repeat.svg' : '/assets/icons/repeat.black.svg'}
      alt=""
      width={16}
      height={16}
    />
  );

  return (
    <section className="panel settings-panel" aria-labelledby="rag-index-heading">
      <div className="panel-head">
        <h2 id="rag-index-heading">Search index</h2>
      </div>

      <div className="settings-row">
        <div>
          <p className="settings-row-label">Last indexed</p>
          <p className="muted settings-row-hint">{lastIndexedLabel}</p>
        </div>
        <GlobalButton
          type="button"
          variant="secondary"
          icon={repeatIcon}
          isLoading={indexing}
          disabled={indexing || !user?.uid}
          onClick={handleReindex}
        >
          Re-index my data
        </GlobalButton>
      </div>

      {success ? (
        <div className={`${authStyles.banner} ${authStyles.bannerSuccess}`} role="status">
          {success}
        </div>
      ) : null}

      {error ? (
        <div className={`${authStyles.banner} ${authStyles.bannerError}`} role="alert">
          <p style={{ margin: 0 }}>{error}</p>
          <GlobalButton
            type="button"
            variant="secondary"
            autoWidth
            disabled={indexing}
            isLoading={indexing}
            onClick={handleReindex}
            style={{ marginTop: 10 }}
          >
            Try again
          </GlobalButton>
        </div>
      ) : null}
    </section>
  );
}
